import { combineReducers, configureStore, createListenerMiddleware } from '@reduxjs/toolkit'
import { persistReducer, persistStore, type PersistConfig } from 'redux-persist'
import { createEncryptedStorage } from '@quickmd-llc/frontend-utils/secure-storage'
// @ts-expect-error TS7016 -- MIGRATION: not approved, fix when encountered
import { createStateSyncMiddleware, initMessageListener } from 'redux-state-sync'
import chartsReducer from '../store/charts/ChartsReducer'
import waitingRoomReducer from '../store/waitingRoom/WaitingRoomReducer'
import visitsPageReducer from '../store/visits/VisitsReducer'
import accountSettingsReducer from '../store/accountSettings/AccountSettingsReducer'
import { authReducer } from '../store/auth/AuthReducer'
import { homeReducer } from '../store/home/HomeReducer'
import { calendarPageReducer } from '../store/calendar/CalendarReducer'
import { resetPasswordReducer } from '../store/reset-password/ResetPasswordReducer'
import { forgotPasswordReducer } from '../store/forgot-password/ForgotPasswordReducer'
import { patientSidePanelReducer } from '../store/patient-side-panel/PatientSidePanelReducer'
import { errorPageReducer } from '../store/error-page/ErrorPageReducer'
import drugRulesSettingsReducer from '../store/drugRulesSettings/drugRulesSettingsReducer'
import flagsReducer from '../store/flags/FlagsReducer'
import { logoutReportingMiddleware } from '../store/auth/logoutReportingMiddleware'
import { logoutTeardownMiddleware } from '../store/auth/logoutTeardownMiddleware'
import { shouldSyncAction } from './syncPredicate'
import { setSessionLivenessSource } from '../api/sessionLiveness'
import { stripInVisitRoom } from './stripInVisitRoom'
import { setPersistor } from './persistorRegistry'
import { LOGOUT } from '../store/auth/AuthConstants'

// Re-exported for the existing `import { EMPTY_ACTION } from '../../app/store'` call sites.
export { EMPTY_ACTION } from './syncPredicate'

const listenerMiddleware = createListenerMiddleware()
const channel = 'QMD2-Provider'
const rootReducer = combineReducers({
  home: homeReducer,
  auth: authReducer,
  resetPassword: resetPasswordReducer,
  calendarPageReducer,
  waitingRoomReducer,
  drugRulesSettingsReducer,
  chartsReducer,
  forgotPassword: forgotPasswordReducer,
  patientSidePanel: patientSidePanelReducer,
  accountSettings: accountSettingsReducer,
  visitsPage: visitsPageReducer,
  error: errorPageReducer,
  flags: flagsReducer,
})
type RootReducerState = ReturnType<typeof rootReducer>

/**
 * Resets every slice on LOGOUT, so a signed-out store holds nothing about the previous
 * clinician.
 *
 * Only five slices (`auth`, `home`, `charts`, `waitingRoom`, `accountSettings`) handled
 * LOGOUT themselves, which left the rest — the visit list, the side panel, drug rules,
 * the calendar — holding that clinician's records in memory after they signed out. That
 * is why `clearAllClientState` had to pause persistence permanently rather than resume
 * it: resuming would have re-serialized those slices straight back to disk under the
 * NEXT session's key. With the reset here, post-logout state is empty and resuming is
 * safe, which is what lets a provider who signs back in without a page load actually
 * have their session persisted. See QMD-4560.
 *
 * `flags` is deliberately kept. It is Firebase Remote Config, not session data, and
 * dropping it sets `flagsFetched` back to false — which `pages/home` reads as "SSO only",
 * hiding the email/password form until Remote Config answers again. It also discards a
 * flag pinned through DevTools.
 *
 * `_persist` never reaches here: `persistReducer` strips it before calling this and adds
 * it back afterwards, so resetting to initial state cannot clobber the rehydration flag.
 */
export const rootReducerWithLogoutReset: typeof rootReducer = (state, action) => {
  if (action.type === LOGOUT && state !== undefined) {
    return { ...rootReducer(undefined, action), flags: state.flags }
  }
  return rootReducer(state, action)
}

// Annotated rather than inferred: letting TS infer `S` from an object carrying
// `transforms` collapses it to `any`, which silently makes `store.getState()` — and
// therefore every `state.x` read in the app — untyped.
const persistConfig: PersistConfig<RootReducerState> = {
  key: 'root',
  // Encrypted rather than `redux-persist/lib/storage`, which writes plain JSON. What
  // lands in `persist:root` here is *other people's* records — the waiting-room queue,
  // the patient roster, chart notes, prescriptions — on a workstation shared between
  // clinicians and shifts. The person who reads it opens DevTools, not a disassembler.
  // It sits at the storage-adapter layer rather than in `transforms` because transforms
  // are synchronous and WebCrypto is not. See QMD-4332.
  storage: createEncryptedStorage(),
  blacklist: ['home', 'resetPassword', 'calendarPageReducer'],
  transforms: [stripInVisitRoom],
}

const persistedReducer = persistReducer(persistConfig, rootReducerWithLogoutReset)

const reduxStateSyncConfig = {
  predicate: shouldSyncAction,
  channel,
  // Skip broadcast-channel's IndexedDB fallback (crashes in Safari private mode / IDB-restricted
  // contexts with "undefined is not an object (evaluating 'a.createObjectStore')") and use
  // localstorage instead. Native BroadcastChannel is still preferred when available. See
  // PROVIDER-WEB-1HJ.
  broadcastChannelOption: { webWorkerSupport: false },
}

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    })
      .prepend(listenerMiddleware.middleware)
      .prepend(
        createStateSyncMiddleware({
          ...reduxStateSyncConfig,
        }),
      )
      .concat(logoutReportingMiddleware, logoutTeardownMiddleware),
})

initMessageListener(store)

// Lives here rather than in `main.tsx` so the logout teardown can reach it without
// importing the React tree. See QMD-4332.
export const persistor = persistStore(store)
setPersistor(persistor)

// Lets `ReactQueryProvider` gate its 401 retry on a live session without importing the
// store — reducers import its `client`, so the direct edge would be a cycle.
setSessionLivenessSource(() => store.getState().auth.loggedIn)
